import { calcCommissionSplits } from "./billing-calculations";
import { DEFAULT_PROVISION_RATE_PCT } from "./constants";

/** Rå rad fra report_commission_by_period (én per prosjekt × eier). */
export interface CommissionReportRow {
  project_id: string;
  project_code: string | null;
  project_name: string;
  commission_locked_at: string;
  bf_actual: number;
  commission_rate_pct: number | null;
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
  split_type: "percent" | "fixed";
  ownership_pct: number | null;
  fixed_amount: number | null;
}

export interface CommissionEmployeeProject {
  project_id: string;
  project_code: string | null;
  project_name: string;
  locked_at: string;
  /** Prosjektets totale frosne BF (bf_actual) — grunnlaget splitten regnes av. */
  bf_actual: number;
  split_type: "percent" | "fixed";
  /** Rå registrert eierandel (kan avvike fra effektiv andel ved normalisering). */
  ownership_pct: number | null;
  fixed_amount: number | null;
  bf_share: number;
  rate_pct: number;
  provision: number;
}

export interface CommissionEmployeeSummary {
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  project_count: number;
  total_bf_share: number;
  total_provision: number;
}

export interface CommissionReport {
  byEmployee: CommissionEmployeeSummary[];
  detailByUser: Record<string, CommissionEmployeeProject[]>;
  grandTotal: number;
}

/**
 * Bygger utbetalingsgrunnlaget. Grupperer rader per prosjekt, kjører den
 * delte calcCommissionSplits med frosset BF + frosset sats, og aggregerer
 * provisjon per ansatt. Kun prosjekter der eieren faktisk får > 0 teller.
 */
export function buildCommissionReport(rows: CommissionReportRow[]): CommissionReport {
  const byProject = new Map<string, CommissionReportRow[]>();
  for (const r of rows) {
    const arr = byProject.get(r.project_id);
    if (arr) arr.push(r);
    else byProject.set(r.project_id, [r]);
  }

  const empMap = new Map<string, CommissionEmployeeSummary>();
  const detailByUser: Record<string, CommissionEmployeeProject[]> = {};

  for (const owners of byProject.values()) {
    const first = owners[0];
    // commission_rate_pct er prosjekt-nivå (frosset ved lukking) — alle rader
    // for samme prosjekt har lik sats, så vi leser fra første rad.
    const ratePct = first.commission_rate_pct ?? DEFAULT_PROVISION_RATE_PCT;
    const splits = calcCommissionSplits(
      first.bf_actual,
      owners.map((o) => ({
        user_id: o.user_id,
        split_type: o.split_type,
        ownership_pct: o.ownership_pct,
        fixed_amount: o.fixed_amount,
      })),
      ratePct / 100,
    );

    for (const s of splits) {
      if (s.provision <= 0) continue;
      const owner = owners.find((o) => o.user_id === s.user_id)!;

      const cur = empMap.get(s.user_id) ?? {
        user_id: s.user_id,
        full_name: owner.full_name ?? "Ukjent",
        avatar_url: owner.avatar_url,
        project_count: 0,
        total_bf_share: 0,
        total_provision: 0,
      };
      cur.project_count += 1;
      cur.total_bf_share += s.bf_share;
      cur.total_provision += s.provision;
      empMap.set(s.user_id, cur);

      (detailByUser[s.user_id] ??= []).push({
        project_id: first.project_id,
        project_code: owner.project_code,
        project_name: owner.project_name,
        locked_at: owner.commission_locked_at,
        bf_actual: first.bf_actual,
        split_type: owner.split_type,
        ownership_pct: owner.ownership_pct,
        fixed_amount: owner.fixed_amount,
        bf_share: s.bf_share,
        rate_pct: ratePct,
        provision: s.provision,
      });
    }
  }

  const byEmployee = [...empMap.values()].sort(
    (a, b) => b.total_provision - a.total_provision,
  );
  const grandTotal = byEmployee.reduce((s, e) => s + e.total_provision, 0);

  return { byEmployee, detailByUser, grandTotal };
}
